import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  TextField,
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Alert,
} from "@mui/material";

const EditBlog = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedImages, setSelectedImages] = useState([]); // New images to add
  const [imagePreviews, setImagePreviews] = useState([]); // Previews for new images
  const [existingImages, setExistingImages] = useState([]); // Existing blog images
  const [imagesToRemove, setImagesToRemove] = useState([]); // IDs of images to remove
  const [author, setAuthor] = useState("");
  const [date, setDate] = useState("");
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const baseUrl = process.env.REACT_APP_BASE_URL;

  // Fetch blog data for editing
  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await axios.get(`${baseUrl}/auth/blog/${id}`);
        const blog = response.data;

        setTitle(blog.title || "");
        setDescription(blog.description || "");
        setAuthor(blog.author || "");
        setDate(blog.date ? blog.date.split("T")[0] : ""); // Format date for input

        // Set existing images (from BlogImages table)
        if (blog.images && blog.images.length > 0) {
          setExistingImages(blog.images);
        } else if (blog.imageUrl) {
          // Fallback for blogs with only featured image
          setExistingImages([{ id: 0, imageUrl: blog.imageUrl, displayOrder: 0 }]);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching blog:", error);
        setError("Failed to load blog data");
        setLoading(false);
      }
    };

    const fetchBlogs = async () => {
      try {
        const response = await axios.get(`${baseUrl}/auth/blogs`);
        setBlogs(response.data);
      } catch (error) {
        console.error("Error fetching blogs for validation:", error);
      }
    };

    if (id) {
      fetchBlog();
      fetchBlogs();
    }
  }, [id, baseUrl]);

  // Handle multiple image selection and preview
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    const maxSize = 5 * 1024 * 1024; // 5MB per file

    const validFiles = [];
    const newPreviews = [];

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        alert(
          `File ${file.name} is not allowed. Only JPG, JPEG, PNG, and GIF files are allowed.`,
        );
        continue;
      }
      if (file.size > maxSize) {
        alert(`File ${file.name} is too large. Maximum size is 5MB.`);
        continue;
      }
      validFiles.push(file);
      newPreviews.push(URL.createObjectURL(file));
    }

    if (validFiles.length > 0) {
      setSelectedImages([...selectedImages, ...validFiles]);
      setImagePreviews([...imagePreviews, ...newPreviews]);
    }
  };

  // Remove a new image from selection
  const handleRemoveNewImage = (index) => {
    const newImages = selectedImages.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);

    URL.revokeObjectURL(imagePreviews[index]);

    setSelectedImages(newImages);
    setImagePreviews(newPreviews);
  };

  // Mark an existing image for removal
  const handleRemoveExistingImage = (imageId) => {
    if (existingImages.length === 1 && selectedImages.length === 0) {
      alert("You must have at least one image. Please add a new image first.");
      return;
    }
    setImagesToRemove([...imagesToRemove, imageId]);
    setExistingImages(existingImages.filter((img) => img.id !== imageId));
  };

  // Clear previews when component unmounts
  useEffect(() => {
    return () => {
      imagePreviews.forEach((preview) => URL.revokeObjectURL(preview));
    };
  }, []);

  const validateForm = () => {
    if (!title.trim() || !description.trim() || !author.trim()) {
      alert("Title, description, and author are required.");
      return false;
    }

    // Check for duplicate title (excluding current blog)
    const existingBlog = blogs.find(
      (blog) =>
        blog.title.toLowerCase() === title.toLowerCase() &&
        blog.id !== parseInt(id),
    );
    if (existingBlog) {
      alert(
        "Blog with this title already exists. Please choose a different title.",
      );
      return false;
    }
    return true;
  };

  const handleUpdateBlog = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
      formData.append("author", author);
      formData.append("date", date || new Date().toISOString().split("T")[0]);

      // Append new images if any
      selectedImages.forEach((image) => {
        formData.append("images", image);
      });

      // Append IDs of images to remove
      if (imagesToRemove.length > 0) {
        formData.append("removeImageIds", JSON.stringify(imagesToRemove));
      }

      try {
        await axios.put(`${baseUrl}/auth/update/blog/${id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        alert("Blog updated successfully");
        navigate("/dashboard/blogs");
      } catch (error) {
        console.error("Error updating blog:", error);
        alert(
          "Blog update failed: " +
            (error.response?.data?.error || "Unknown error"),
        );
      }
    }
  };

  const handleCancel = () => {
    navigate("/dashboard/blogs");
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Typography variant="h4" component="h1" sx={{ mt: 2 }} gutterBottom>
          Edit Blog Post
        </Typography>
        <Typography>Loading blog data...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Typography variant="h4" component="h1" sx={{ mt: 2 }} gutterBottom>
          Edit Blog Post
        </Typography>
        <Alert severity="error">{error}</Alert>
        <Button
          variant="contained"
          onClick={() => navigate("/dashboard/blogs")}
          sx={{ mt: 2 }}
        >
          Back to Blogs
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" component="h1" sx={{ mt: 2 }} gutterBottom>
        Edit Blog Post
      </Typography>
      <form onSubmit={handleUpdateBlog}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              margin="normal"
              label="Title"
              variant="outlined"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              margin="normal"
              label="Description"
              variant="outlined"
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Blog Images
            </Typography>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageChange}
              style={{ margin: "8px 0", display: "block" }}
            />

            {/* Existing images */}
            {existingImages.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Current Images:
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                    gap: 2,
                  }}
                >
                  {existingImages.map((img, index) => (
                    <Box
                      key={img.id || index}
                      sx={{
                        position: "relative",
                        borderRadius: "8px",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={`${baseUrl}${img.imageUrl}`}
                        alt={`Existing ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "150px",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/150/4B5563/FFFFFF?text=Image";
                        }}
                      />
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleRemoveExistingImage(img.id)}
                        sx={{
                          position: "absolute",
                          top: 5,
                          right: 5,
                          minWidth: "auto",
                          padding: "4px 8px",
                        }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* New images to be added */}
            {imagePreviews.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  New Images to Add:
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                    gap: 2,
                  }}
                >
                  {imagePreviews.map((preview, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: "relative",
                        borderRadius: "8px",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={preview}
                        alt={`New ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "150px",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                      />
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleRemoveNewImage(index)}
                        sx={{
                          position: "absolute",
                          top: 5,
                          right: 5,
                          minWidth: "auto",
                          padding: "4px 8px",
                        }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="normal"
              label="Author"
              variant="outlined"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="normal"
              label="Publication Date"
              variant="outlined"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, mb: 16, display: "flex", gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ width: "20%" }}
          >
            Update Blog
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={handleCancel}
            sx={{ width: "20%" }}
          >
            Cancel
          </Button>
        </Box>
      </form>
    </Container>
  );
};

export default EditBlog;
